import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../../../services/AuthService';
import './ParentSignupForm.css';

/**
 * Parent account-creation form. Registers a real account (email-verification
 * flow); the parent role is granted on first parent login.
 */
function ParentSignupForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
    relationship: '', phone: '', academicAlerts: true, termsAccepted: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!formData.termsAccepted) {
      setError('Please accept the terms and privacy policy.');
      return;
    }

    setLoading(true);
    try {
      const result = await registerUser({
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        password: undefined, // password is set after email verification
      });
      if (!result.success) {
        const raw = (result.message || '').toLowerCase();
        setError(raw.includes('already')
          ? 'An account with that email already exists. Try signing in instead.'
          : (result.message || 'Registration failed. Please try again.'));
        return;
      }
      navigate('/login', {
        state: { info: 'Check your email to verify your account, then sign in as a Parent.' },
      });
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="parent-signup-form" onSubmit={handleSubmit}>
      <h2 className="parent-signup-form__title">Create Parent Account</h2>
      <p className="parent-signup-form__subtitle">
        Set up your account to monitor your student's academic progress.
      </p>

      {error && <div className="parent-signup-form__error">{error}</div>}

      <div className="parent-signup-form__row">
        <div className="parent-signup-form__field">
          <label htmlFor="parent-first">First Name</label>
          <input id="parent-first" type="text" value={formData.firstName} onChange={e => update('firstName', e.target.value)} required />
        </div>
        <div className="parent-signup-form__field">
          <label htmlFor="parent-last">Last Name</label>
          <input id="parent-last" type="text" value={formData.lastName} onChange={e => update('lastName', e.target.value)} required />
        </div>
      </div>

      <div className="parent-signup-form__field">
        <label htmlFor="parent-email">Email Address</label>
        <input id="parent-email" type="email" value={formData.email} onChange={e => update('email', e.target.value)} required />
      </div>

      <div className="parent-signup-form__row">
        <div className="parent-signup-form__field">
          <label htmlFor="parent-password">Password</label>
          <input id="parent-password" type="password" value={formData.password} onChange={e => update('password', e.target.value)} required minLength={8} />
        </div>
        <div className="parent-signup-form__field">
          <label htmlFor="parent-confirm">Confirm Password</label>
          <input id="parent-confirm" type="password" value={formData.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} required />
        </div>
      </div>

      <div className="parent-signup-form__field">
        <label htmlFor="parent-relationship">Relationship to Student</label>
        <select id="parent-relationship" value={formData.relationship} onChange={e => update('relationship', e.target.value)} required>
          <option value="">Select...</option>
          <option value="Parent">Parent</option>
          <option value="Guardian">Guardian</option>
          <option value="Grandparent">Grandparent</option>
          <option value="Counselor">Counselor</option>
          <option value="Other">Other authorized adult</option>
        </select>
      </div>

      <div className="parent-signup-form__field">
        <label htmlFor="parent-phone">Phone Number (optional)</label>
        <input id="parent-phone" type="tel" value={formData.phone} onChange={e => update('phone', e.target.value)} placeholder="(555) 123-4567" />
      </div>

      <label className="parent-signup-form__checkbox">
        <input type="checkbox" checked={formData.academicAlerts} onChange={e => update('academicAlerts', e.target.checked)} />
        <span>Send me academic alert emails</span>
      </label>

      <label className="parent-signup-form__checkbox">
        <input type="checkbox" checked={formData.termsAccepted} onChange={e => update('termsAccepted', e.target.checked)} required />
        <span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
      </label>

      <button type="submit" className="parent-signup-form__submit" disabled={loading}>
        {loading ? 'Creating Account...' : 'Create Parent Account'}
      </button>
    </form>
  );
}

export default ParentSignupForm;
